<template>
  <div class="musubi-page musubi-page--profile">
    <header class="musubi-page__head">
      <h1>My profile</h1>
      <p class="text-tertiary">Your account details, password and preferences.</p>
    </header>

    <div v-if="loading" class="text-tertiary small py-3">Loading…</div>

    <template v-else>
      <!-- Account details -->
      <section class="pf-card">
        <h2 class="pf-h">Account details</h2>
        <div v-if="detailsSaved" class="pf-banner pf-banner--ok"><CygnusIcon name="check" class="me-1" />Details saved.</div>
        <div v-if="detailsError" class="pf-banner pf-banner--err"><CygnusIcon name="warning" class="me-1" />{{ detailsError }}</div>

        <div class="pf-grid">
          <label class="pf-field">
            <span class="pf-label">First name</span>
            <input v-model.trim="form.first_name" type="text" class="pf-input" />
          </label>
          <label class="pf-field">
            <span class="pf-label">Last name</span>
            <input v-model.trim="form.last_name" type="text" class="pf-input" />
          </label>
        </div>
        <label class="pf-field">
          <span class="pf-label">Email address</span>
          <input v-model.trim="form.email" type="email" class="pf-input" />
          <span class="pf-hint">Changing this updates the address you sign in with.</span>
        </label>
        <div class="pf-actions">
          <button class="btn btn-commit" :disabled="savingDetails" @click="saveDetails">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="savingDetails" />
            <CygnusIcon name="download" class="me-1" v-else />Save details
          </button>
        </div>
      </section>

      <!-- Password -->
      <section class="pf-card">
        <h2 class="pf-h">Change password</h2>
        <div v-if="pwSaved" class="pf-banner pf-banner--ok"><CygnusIcon name="check" class="me-1" />Password changed.</div>
        <div v-if="pwError" class="pf-banner pf-banner--err"><CygnusIcon name="warning" class="me-1" />{{ pwError }}</div>

        <label class="pf-field">
          <span class="pf-label">Current password</span>
          <input v-model="pw.current_password" type="password" class="pf-input" autocomplete="current-password" />
        </label>
        <div class="pf-grid">
          <label class="pf-field">
            <span class="pf-label">New password</span>
            <input v-model="pw.new_password" type="password" class="pf-input" autocomplete="new-password" />
            <span class="pf-hint">At least 10 characters, with a mix of upper and lower case, numbers or symbols.</span>
          </label>
          <label class="pf-field">
            <span class="pf-label">Confirm new password</span>
            <input v-model="pw.confirm" type="password" class="pf-input" autocomplete="new-password" />
          </label>
        </div>
        <div class="pf-actions">
          <button class="btn btn-commit" :disabled="savingPw || !pwReady" @click="savePassword">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="savingPw" />
            <CygnusIcon name="lock" class="me-1" v-else />Update password
          </button>
        </div>
      </section>

      <!-- Preferences -->
      <section class="pf-card">
        <h2 class="pf-h">Preferences</h2>
        <div class="pf-pref">
          <div class="pf-pref__body">
            <div class="pf-pref__title">Theme</div>
            <div class="pf-pref__sub">Light or dark. Also toggleable from the account menu; saved on this device.</div>
          </div>
          <div class="pf-seg">
            <button v-for="t in ['light','dark']" :key="t" class="pf-seg__btn" :class="{ 'is-on': theme === t }" @click="setTheme(t)">
              <svg v-if="t === 'light'" class="cygnus-icon" viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg><svg v-else class="cygnus-icon" viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg> {{ t }}
            </button>
          </div>
        </div>
        <div class="pf-pref">
          <div class="pf-pref__body">
            <div class="pf-pref__title">Notifications</div>
            <div class="pf-pref__sub">Choose which emails and in-app alerts you receive.</div>
          </div>
          <a class="btn btn-ghost btn-sm" href="/profile/notification-preferences">Manage notifications</a>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * MyProfile — the current user's own account page (/profile). Edits name/email,
 * changes password (current password required), and surfaces theme + a link to
 * notification preferences. Reads/writes GET/POST /api/v1/me/profile. Fixes the
 * previously-dead LhsHeaderCard "My profile" link (which already points here).
 */
import useApi from "@apexl/icarus-core/src/services/api";
import { getStoredTheme, applyTheme } from "@root/services/theme";

export default {
  name: "MyProfile",
  props: { apiBase: { type: String, default: "/api/v1" }, componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      form: { first_name: "", last_name: "", email: "" },
      savingDetails: false, detailsSaved: false, detailsError: "",
      pw: { current_password: "", new_password: "", confirm: "" },
      savingPw: false, pwSaved: false, pwError: "",
      theme: "light",
    };
  },
  computed: {
    pwReady() {
      return this.pw.current_password && this.pw.new_password && this.pw.new_password === this.pw.confirm;
    },
  },
  mounted() {
    this.theme = getStoredTheme() || "light";
    this.load();
  },
  methods: {
    unwrap(res) { return res?.data?.data ?? res?.data ?? res; },
    async load() {
      this.loading = true;
      try {
        const d = this.unwrap(await this.api.get(this.apiBase + "/me/profile"));
        const p = (d && d.profile) || {};
        this.form.first_name = p.first_name || "";
        this.form.last_name = p.last_name || "";
        this.form.email = p.email || "";
      } catch (e) { this.detailsError = "Could not load your profile."; }
      finally { this.loading = false; }
    },
    async saveDetails() {
      this.savingDetails = true; this.detailsSaved = false; this.detailsError = "";
      try {
        await this.api.post(this.apiBase + "/me/profile", {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          email: this.form.email,
        });
        this.detailsSaved = true;
      } catch (e) {
        this.detailsError = e?.response?.data?.error || e?.message || "Could not save your details.";
      } finally { this.savingDetails = false; }
    },
    async savePassword() {
      if (!this.pwReady) { this.pwError = "New passwords must match."; return; }
      this.savingPw = true; this.pwSaved = false; this.pwError = "";
      try {
        await this.api.post(this.apiBase + "/me/profile", {
          current_password: this.pw.current_password,
          new_password: this.pw.new_password,
        });
        this.pwSaved = true;
        this.pw = { current_password: "", new_password: "", confirm: "" };
      } catch (e) {
        this.pwError = e?.response?.data?.error || e?.message || "Could not change your password.";
      } finally { this.savingPw = false; }
    },
    setTheme(t) {
      this.theme = t;
      applyTheme(t);
    },
  },
};
</script>

<style scoped>
.musubi-page--profile { padding: var(--m-space-6, 32px); max-width: 680px; }
.musubi-page__head h1 { font-weight: 750; }
.text-tertiary { color: var(--m-text-tertiary); }
.pf-card {
  margin-top: 18px; background: var(--m-bg-card); border: 1px solid var(--m-border);
  border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); padding: 22px;
}
.pf-h { font-size: 0.98rem; font-weight: 700; margin: 0 0 14px; color: var(--m-text-primary); }
.pf-banner { border-radius: var(--m-radius); padding: 9px 12px; font-size: 0.82rem; margin-bottom: 14px; }
.pf-banner--ok { background: color-mix(in srgb, var(--m-status-success) 14%, var(--m-bg-card)); color: var(--m-status-success); border: 1px solid color-mix(in srgb, var(--m-status-success) 34%, transparent); }
.pf-banner--err { background: color-mix(in srgb, var(--m-status-danger) 12%, var(--m-bg-card)); color: var(--m-status-danger); border: 1px solid color-mix(in srgb, var(--m-status-danger) 32%, transparent); }
.pf-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 560px) { .pf-grid { grid-template-columns: 1fr; } }
.pf-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 16px; }
.pf-label { font-size: 0.78rem; font-weight: 700; color: var(--m-text-secondary); }
.pf-input {
  border: 1px solid var(--m-border-strong); border-radius: var(--m-radius); padding: 9px 11px;
  font: inherit; font-size: 0.9rem; color: var(--m-text-primary); background: var(--m-bg-content);
}
.pf-input:focus { outline: none; border-color: var(--m-accent); }
.pf-hint { font-size: 0.72rem; color: var(--m-text-tertiary); }
.pf-actions { margin-top: 2px; }
.btn { display: inline-flex; align-items: center; font: inherit; font-weight: 650; border-radius: 8px; padding: 9px 16px; cursor: pointer; border: 1px solid transparent; text-decoration: none; }
.btn:disabled { opacity: .55; cursor: not-allowed; }
.btn-sm { padding: 7px 12px; font-size: 0.82rem; }
.btn-commit { background: var(--m-accent); color: var(--m-accent-text); border-color: var(--m-accent); font-weight: 700; }
.btn-commit:hover:not(:disabled) { background: var(--m-accent-hover); }
.btn-ghost { background: transparent; border: 1px solid var(--m-border-strong); color: var(--m-text-secondary); }
.btn-ghost:hover { border-color: var(--m-accent); color: var(--m-accent); }
.pf-pref { display: flex; align-items: center; gap: 14px; padding: 12px 0; }
.pf-pref + .pf-pref { border-top: 1px solid var(--m-border); }
.pf-pref__body { flex: 1; min-width: 0; }
.pf-pref__title { font-size: 0.88rem; font-weight: 650; color: var(--m-text-primary); }
.pf-pref__sub { font-size: 0.74rem; color: var(--m-text-tertiary); margin-top: 2px; }
.pf-seg { display: inline-flex; border: 1px solid var(--m-border-strong); border-radius: 999px; overflow: hidden; }
.pf-seg__btn { display: inline-flex; align-items: center; gap: 5px; background: var(--m-bg-content); border: none; padding: 6px 13px; font: inherit; font-size: 0.8rem; font-weight: 600; text-transform: capitalize; color: var(--m-text-secondary); cursor: pointer; }
.pf-seg__btn.is-on { background: var(--m-accent); color: var(--m-accent-text); }
</style>
